import React, { useState, useEffect, useRef } from 'react';
import ApiClient from '../api/ApiClient';

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString();
}

function SignalActionBadge({ action }) {
  const colors = { Buy: 'success', Sell: 'danger', Money: 'warning' };
  return (
    <span className={`badge bg-${colors[action] || 'secondary'} me-1`}>{action}</span>
  );
}

function CombinedResultTable({ signals }) {
  if (!signals || signals.length === 0) {
    return <p className="text-muted small">No signals</p>;
  }
  const sorted = [...signals].sort((a, b) => new Date(b.Time) - new Date(a.Time));
  return (
    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
      <table className="table table-sm table-striped mb-0">
        <thead className="table-light sticky-top">
          <tr>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => (
            <tr key={i}>
              <td className="small">{formatDate(s.Time)}</td>
              <td><SignalActionBadge action={s.Action} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const CombineModels = () => {
  // All models with their latest signal file info
  const [modelsData, setModelsData] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState(null);

  // Model/tag picker state
  const [modelSearch, setModelSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [tagLookupLoading, setTagLookupLoading] = useState(false);
  const [tagSignalFileInfo, setTagSignalFileInfo] = useState(null); // for current picker
  const dropdownRef = useRef(null);

  // Selected rows table
  // Each row: { rowId, model, tag, signalFileInfo }
  // signalFileInfo: null = no file found, or { CreateTime, Id } from latest signal file for that model+tag
  const [rows, setRows] = useState([]);

  // Per-model calculate state
  const [calculating, setCalculating] = useState({}); // { rowId: bool }
  const [calculateResults, setCalculateResults] = useState({}); // { rowId: { success, message } }

  // Real-time candles option
  const [useRealTimeCandles, setUseRealTimeCandles] = useState(false);

  // Combine state
  const [combining, setCombining] = useState(false);
  const [combineResult, setCombineResult] = useState(null);
  const [combineError, setCombineError] = useState(null);

  // Signal combination mode (mirrors python/sigMesh2.py multi_mesh modes)
  const COMBINE_MODE_OPTIONS = [
    { value: 'CbSoM', label: 'cBSoM — unanimous Buy / unanimous Sell / Mixed → Money (default)' },
    { value: 'CBoS',  label: 'cBoS — Buy only when all agree Buy, otherwise Sell' },
    { value: 'CSoB',  label: 'cSoB — Sell only when all agree Sell, otherwise Buy' },
  ];
  const [combineMode, setCombineMode] = useState(COMBINE_MODE_OPTIONS[0]);

  useEffect(() => {
    loadModels();
  }, []);

  // Whenever the picked model or tag changes, look up the actual latest signal file for that combo
  useEffect(() => {
    if (!selectedModel || !selectedTag) {
      setTagSignalFileInfo(null);
      return;
    }
    let cancelled = false;
    async function fetchTagFile() {
      setTagLookupLoading(true);
      try {
        const data = await ApiClient.searchSignalFiles({
          ModelId: selectedModel.ModelId,
          Tag: selectedTag,
          Limit: 1,
          Offset: 0,
        });
        if (!cancelled) {
          const item = data.Items && data.Items[0];
          setTagSignalFileInfo(item ? { Id: item.Id, CreateTime: item.CreateTime, RealtimeCandlesUsed: item.RealtimeCandlesUsed || false } : null);
        }
      } catch (err) {
        if (!cancelled) setTagSignalFileInfo(null);
      } finally {
        if (!cancelled) setTagLookupLoading(false);
      }
    }
    fetchTagFile();
    return () => { cancelled = true; };
  }, [selectedModel, selectedTag]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadModels() {
    setLoadingModels(true);
    setModelsError(null);
    try {
      const data = await ApiClient.getRecentSignals({
        Limit: 1000,
        Offset: 0,
        LimitSignals: 1,
      });
      setModelsData(data.Items || []);
    } catch (err) {
      console.error('Failed to load models', err);
      setModelsError(err.message || 'Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  }

  // Filtered models for dropdown
  const filteredModels = modelsData.filter(m => {
    const q = modelSearch.toLowerCase();
    if (!q) return true;
    return (
      (m.ModelName || '').toLowerCase().includes(q) ||
      (m.ModelFileName || '').toLowerCase().includes(q)
    );
  });

  function selectModel(model) {
    setSelectedModel(model);
    setModelSearch(model.ModelName || model.ModelFileName || `#${model.ModelId}`);
    setShowDropdown(false);
    // Default tag: first available. The recentsignals response no longer
    // exposes the model's ActiveSignalTag, so we always start with the
    // first tag from SignalTags.
    const tags = model.SignalTags || [];
    const defaultTag = tags[0] || '';
    setSelectedTag(defaultTag);
    setTagSignalFileInfo(null);
  }

  function addRow() {
    if (!selectedModel || !selectedTag) return;
    const rowId = `${selectedModel.ModelId}-${selectedTag}-${Date.now()}`;
    setRows(prev => [...prev, {
      rowId,
      model: selectedModel,
      tag: selectedTag,
      signalFileInfo: tagSignalFileInfo, // null means no file found
    }]);
  }

  function removeRow(rowId) {
    setRows(prev => prev.filter(r => r.rowId !== rowId));
    setCalculateResults(prev => { const n = { ...prev }; delete n[rowId]; return n; });
  }

  function moveRow(rowId, direction) {
    setRows(prev => {
      const idx = prev.findIndex(r => r.rowId === rowId);
      if (idx < 0) return prev;
      const newRows = [...prev];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= newRows.length) return prev;
      [newRows[idx], newRows[targetIdx]] = [newRows[targetIdx], newRows[idx]];
      return newRows;
    });
  }

  // Execute one model and update all rows that belong to it.
  // rowsForModel: the subset of `rows` that share this modelId.
  async function calculateModel(modelId, rowsForModel) {
    const rowIds = rowsForModel.map(r => r.rowId);
    // Mark all rows for this model as calculating
    setCalculating(prev => {
      const next = { ...prev };
      rowIds.forEach(id => { next[id] = true; });
      return next;
    });
    setCalculateResults(prev => {
      const next = { ...prev };
      rowIds.forEach(id => { next[id] = null; });
      return next;
    });
    setCombineResult(null);
    try {
      const result = await ApiClient.executeModel(modelId, false, useRealTimeCandles);
      // result.SignalFileIds is { [tag]: signalFileId }
      for (const row of rowsForModel) {
        const newFileId = result.SignalFileIds && result.SignalFileIds[row.tag];
        let updatedSignalFileInfo = null;
        if (newFileId) {
          try {
            const sf = await ApiClient.getSignalFile(newFileId);
            updatedSignalFileInfo = { Id: sf.Id, CreateTime: sf.CreateTime, RealtimeCandlesUsed: sf.RealtimeCandlesUsed || false };
          } catch {
            updatedSignalFileInfo = { Id: newFileId, CreateTime: new Date().toISOString() };
          }
        } else {
          try {
            const data = await ApiClient.searchSignalFiles({
              ModelId: modelId,
              Tag: row.tag,
              Limit: 1,
              Offset: 0,
            });
            const item = data.Items && data.Items[0];
            if (item) updatedSignalFileInfo = { Id: item.Id, CreateTime: item.CreateTime, RealtimeCandlesUsed: item.RealtimeCandlesUsed || false };
          } catch { /* leave null */ }
        }
        if (updatedSignalFileInfo) {
          setRows(prev => prev.map(r =>
            r.rowId === row.rowId ? { ...r, signalFileInfo: updatedSignalFileInfo } : r
          ));
        }
        setCalculateResults(prev => ({
          ...prev,
          [row.rowId]: { success: true, message: `Calculated. Signal file #${updatedSignalFileInfo?.Id ?? 'saved'}.` }
        }));
      }
      loadModels();
    } catch (err) {
      rowIds.forEach(id => {
        setCalculateResults(prev => ({
          ...prev,
          [id]: { success: false, message: err.message || 'Calculation failed' }
        }));
      });
    } finally {
      setCalculating(prev => {
        const next = { ...prev };
        rowIds.forEach(id => { next[id] = false; });
        return next;
      });
    }
  }

  async function calculateAll() {
    // Group rows by ModelId to avoid redundant executions
    const byModel = {};
    for (const row of rows) {
      const mid = row.model.ModelId;
      if (!byModel[mid]) byModel[mid] = [];
      byModel[mid].push(row);
    }
    for (const [modelId, modelRows] of Object.entries(byModel)) {
      await calculateModel(Number(modelId), modelRows);
    }
  }

  async function calculateMissing() {
    const missingRows = rows.filter(r => !r.signalFileInfo?.Id);
    // Group by ModelId
    const byModel = {};
    for (const row of missingRows) {
      const mid = row.model.ModelId;
      if (!byModel[mid]) byModel[mid] = [];
      byModel[mid].push(row);
    }
    for (const [modelId, modelRows] of Object.entries(byModel)) {
      await calculateModel(Number(modelId), modelRows);
    }
  }

  async function combineSignals() {
    setCombineError(null);
    setCombineResult(null);

    const signalFileIds = rows
      .map(r => r.signalFileInfo?.Id)
      .filter(id => !!id);

    if (signalFileIds.length < 2) {
      setCombineError('At least 2 rows with available signal files are required to combine.');
      return;
    }

    setCombining(true);
    try {
      const combined = await ApiClient.combineSignalFiles(signalFileIds, combineMode.value);
      setCombineResult({ signals: combined.Signals || [], fileCount: signalFileIds.length });
    } catch (err) {
      setCombineError(err.message || 'Combine failed');
    } finally {
      setCombining(false);
    }
  }

  const canAdd = !!selectedModel && !!selectedTag && !tagLookupLoading;
  const anyCalculating = Object.values(calculating).some(Boolean);

  const [downloadingCombined, setDownloadingCombined] = useState(false);

  async function downloadCombinedSignal() {
    if (!combineResult || combineResult.signals.length === 0) return;
    setDownloadingCombined(true);
    try {
      const signalFileIds = rows
        .filter(r => r.signalFileInfo?.Id)
        .map(r => r.signalFileInfo.Id);
      const { url, filename } = await ApiClient.downloadCombinedSignalFile(signalFileIds, combineMode.value);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (exc) {
      alert('Download failed: ' + (exc.message || exc));
    } finally {
      setDownloadingCombined(false);
    }
  }

  return (
    <div className="container-fluid mt-3">
      <h2 className="mb-4">Combine Models</h2>

      {/* Model + Tag Picker */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Add Model</h5></div>
        <div className="card-body">
          {modelsError && <div className="alert alert-danger py-2">{modelsError}</div>}

          <div className="row g-3 align-items-end">
            {/* Model search */}
            <div className="col-md-5">
              <label className="form-label">Model</label>
              <div className="position-relative" ref={dropdownRef}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={loadingModels ? 'Loading models…' : 'Search model…'}
                  value={modelSearch}
                  disabled={loadingModels}
                  onChange={e => {
                    setModelSearch(e.target.value);
                    setSelectedModel(null);
                    setSelectedTag('');
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                />
                {showDropdown && filteredModels.length > 0 && (
                  <div
                    className="border rounded bg-white shadow-sm position-absolute w-100"
                    style={{ zIndex: 1050, maxHeight: 260, overflowY: 'auto', top: '100%' }}
                  >
                    {filteredModels.map(m => (
                      <div
                        key={m.ModelId}
                        className="px-3 py-2 border-bottom"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => selectModel(m)}
                      >
                        <div className="fw-semibold">{m.ModelName || m.ModelFileName || `#${m.ModelId}`}</div>
                        {m.ModelName && <div className="small text-muted">{m.ModelFileName}</div>}
                        {!m.LatestCorrelationId
                          ? <span className="badge bg-secondary small">No signal file</span>
                          : <span className="badge bg-success small">Has signal files ({Object.keys(m.RecentSignals || {}).length} tag{Object.keys(m.RecentSignals || {}).length === 1 ? '' : 's'})</span>
                        }
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tag selector */}
            <div className="col-md-3">
              <label className="form-label">Signal Tag</label>
              {selectedModel && (selectedModel.SignalTags || []).length > 0 ? (
                <select
                  className="form-select"
                  value={selectedTag}
                  onChange={e => setSelectedTag(e.target.value)}
                >
                  {(selectedModel.SignalTags || []).map(tag => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder="No tags"
                  disabled
                  value=""
                />
              )}
            </div>

            {/* Add button */}
            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                disabled={!canAdd}
                onClick={addRow}
              >
                {tagLookupLoading
                  ? <><span className="spinner-border spinner-border-sm me-1" />Checking…</>
                  : '+ Add'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selected models table */}
      {rows.length > 0 && (
        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Selected Models ({rows.length})</h5>
            <div className="d-flex gap-2 align-items-center">
              <div className="d-flex align-items-center">
                <label className="form-label small text-muted me-2 mb-0" htmlFor="combineModeSelect">
                  Combine Mode:
                </label>
                <select
                  id="combineModeSelect"
                  className="form-select form-select-sm"
                  style={{ minWidth: 320 }}
                  value={combineMode.value}
                  onChange={e => setCombineMode(
                    COMBINE_MODE_OPTIONS.find(o => o.value === e.target.value) || COMBINE_MODE_OPTIONS[0]
                  )}
                  disabled={rows.length < 2}
                  title="How to combine signals from the selected models"
                >
                  {COMBINE_MODE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-check mb-0 me-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useRealTimeCandlesCombine"
                  checked={useRealTimeCandles}
                  onChange={e => setUseRealTimeCandles(e.target.checked)}
                  disabled={anyCalculating}
                />
                <label className="form-check-label small" htmlFor="useRealTimeCandlesCombine">
                  Use real-time candles
                </label>
              </div>
              <button
                className="btn btn-sm btn-outline-warning"
                disabled={anyCalculating || rows.every(r => r.signalFileInfo?.Id)}
                onClick={calculateMissing}
                title="Calculate only models with missing signal files"
              >
                {anyCalculating ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Calculating…</>
                ) : 'Calculate Missing'}
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={anyCalculating}
                onClick={calculateAll}
                title="Calculate all models"
              >
                {anyCalculating ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Calculating…</>
                ) : 'Calculate All'}
              </button>
              <button
                className="btn btn-sm btn-success"
                disabled={combining || rows.filter(r => r.signalFileInfo?.Id).length < 2}
                onClick={combineSignals}
                title="Combine signals"
              >
                {combining ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Combining…</>
                ) : 'Combine'}
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Model Name</th>
                    <th>File Name</th>
                    <th>Tag</th>
                    <th>Signal File</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const calcResult = calculateResults[row.rowId];
                    const isCalc = !!calculating[row.rowId];
                    return (
                      <tr key={row.rowId}>
                        <td className="text-muted small">{idx + 1}</td>
                        <td>
                          <span className="fw-semibold">
                            {row.model.ModelName || `#${row.model.ModelId}`}
                          </span>
                        </td>
                        <td>
                          <code className="small">{row.model.ModelFileName || '—'}</code>
                        </td>
                        <td>
                          <span className="badge bg-info text-dark">{row.tag}</span>
                        </td>
                        <td>
                          {row.signalFileInfo === null ? (
                            <span className="text-danger small">
                              <i className="me-1">⚠</i>No signal file
                            </span>
                          ) : row.signalFileInfo.CreateTime ? (
                            <span className="text-success small">
                              <i className="me-1">✓</i>
                              {formatDate(row.signalFileInfo.CreateTime)}
                              {row.signalFileInfo.Id && (
                                <span className="text-muted ms-1">(#{row.signalFileInfo.Id})</span>
                              )}
                              {row.signalFileInfo.RealtimeCandlesUsed && (
                                <span className="badge bg-warning text-dark ms-2" title="Calculated with real-time candles">RT Candles</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-warning small">
                              <i className="me-1">?</i>File exists (tag not active)
                            </span>
                          )}
                          {calcResult && (
                            <div className={`small mt-1 ${calcResult.success ? 'text-success' : 'text-danger'}`}>
                              {calcResult.message}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              disabled={idx === 0}
                              onClick={() => moveRow(row.rowId, -1)}
                              title="Move up"
                            >↑</button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              disabled={idx === rows.length - 1}
                              onClick={() => moveRow(row.rowId, 1)}
                              title="Move down"
                            >↓</button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              disabled={isCalc}
                              onClick={() => calculateModel(row.model.ModelId, [row])}
                              title="Calculate this model"
                            >
                              {isCalc
                                ? <span className="spinner-border spinner-border-sm" />
                                : 'Calc'}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeRow(row.rowId)}
                              title="Remove"
                            >✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {combineError && (
            <div className="card-footer">
              <div className="alert alert-danger mb-0 py-2">{combineError}</div>
            </div>
          )}
        </div>
      )}

      {/* Combined result */}
      {combineResult && (
        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              Combined Signal
              <span className="text-muted fw-normal small ms-2">
                (from {combineResult.fileCount} signal files)
              </span>
            </h5>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={combineResult.signals.length === 0 || downloadingCombined}
              onClick={downloadCombinedSignal}
              title="Download combined signal as CSV"
            >
              {downloadingCombined
                ? <><span className="spinner-border spinner-border-sm me-1" />Downloading…</>
                : 'Download'}
            </button>
          </div>
          <div className="card-body">
            {combineResult.signals.length === 0 ? (
              <div className="alert alert-warning mb-0">No combined signals produced.</div>
            ) : (
              <CombinedResultTable signals={combineResult.signals} />
            )}
          </div>
        </div>
      )}

      {rows.length === 0 && !loadingModels && (
        <div className="text-center text-muted py-5">
          Add models above to get started.
        </div>
      )}
    </div>
  );
};

